import produce from "immer";
import type React from "react";
import { FormattedHTMLMessage, type InjectedIntlProps, injectIntl } from "react-intl";

import { logger } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { type CustomFormFieldConfig, type VerificationService } from "../../../lib/types/types";
import { getFormFieldSelectOptions } from "../../../lib/utils/structures/FormSelectChoice";
import { FormFieldCheckbox } from "../FormFieldCheckbox/FormFieldCheckbox";
import { FormFieldText } from "../FormFieldText/FormFieldText";
import { FormFieldSelect } from "../FormFieldSelect/FormFieldSelect";
import { DateComponent } from "../../DateComponent/DateComponent";
import { FormFieldRadio } from "../FormFieldRadio/FormFieldRadio";

export const FormFieldConfigType = {
  TEXT: "text",
  CHECKBOX: "checkbox",
  SELECT: "select",
  RADIO: "radio",
  DATE: "date",
  DATEYM: "dateYm",
} as const;

export interface FormFieldCustomProps extends InjectedIntlProps {
  config: CustomFormFieldConfig;
  verificationService: VerificationService;
}

export interface FormFieldBaseComponentProps<TFieldValue> {
  fieldId: string; // should be camelCase
  value: TFieldValue;
  onChange: (value: TFieldValue) => void;
  label: React.ReactNode; // Label is always required for screen readers
  errorMessage?: React.ReactNode;
}

export const getMessageIdForLabel = (config: CustomFormFieldConfig) => `${config.fieldId}Label`;
export const getMessageIdForExplanation = (config: CustomFormFieldConfig) =>
  `${config.fieldId}Explanation`;
export const getMessageIdForPlaceholder = (config: CustomFormFieldConfig) =>
  `${config.fieldId}Placeholder`;

/**
 * Custom form field component.
 * Implementors can render a form field passing in config using setOptions, rather than
 * having to create an entire React project and override component(s).
 */
export const FormFieldCustom = injectIntl(
  ({ config, verificationService, intl }: FormFieldCustomProps) => {
    const currentValue = getSafe(() => verificationService.viewModel.metadata[config.fieldId]);
    const errorId = getSafe(() => verificationService.fieldValidationErrors[config.fieldId]);

    const onChange = (value) => {
      verificationService.updateViewModel(
        produce(verificationService.viewModel, (draft) => {
          draft.metadata = draft.metadata || {};
          draft.metadata[config.fieldId] = value;
        }),
      );
      verificationService.updateFieldValidationErrors(
        produce(verificationService.fieldValidationErrors, (draft) => {
          const errorId: ReturnType<typeof config.validate> = config.validate(value);
          if (errorId === undefined) {
            delete draft[config.fieldId];
          }
          // Don't set error. Only do that when submit is clicked, same as jslib's built-in fields
        }),
      );
    };

    switch (config.fieldType) {
      case FormFieldConfigType.TEXT:
        return (
          <FormFieldText
            fieldId={config.fieldId}
            value={`${currentValue || ""}`}
            onChange={onChange}
            disabled={config.disabled}
            placeholder={intl.formatHTMLMessage({
              id: getMessageIdForPlaceholder(config),
              defaultMessage: config.fieldId,
            })}
            label={
              <FormattedHTMLMessage
                id={getMessageIdForLabel(config)}
                defaultMessage={config.fieldId}
              />
            }
            explanation={
              getSafe(() => intl.messages[getMessageIdForExplanation(config)]) && (
                <FormattedHTMLMessage id={getMessageIdForExplanation(config)} />
              )
            }
            errorMessage={
              errorId && (
                <FormattedHTMLMessage
                  id={`errorId.${errorId}`}
                  defaultMessage={`Invalid ${config.fieldId}`}
                />
              )
            }
            showPlaceholderAndHideLabel={config.showPlaceholderAndHideLabel}
            required={config.required}
          />
        );
      case FormFieldConfigType.CHECKBOX:
        return (
          <FormFieldCheckbox
            fieldId={config.fieldId}
            value={typeof currentValue === "boolean" ? currentValue : false}
            onChange={onChange}
            disabled={config.disabled}
            label={
              <FormattedHTMLMessage
                id={getMessageIdForLabel(config)}
                defaultMessage={config.fieldId}
              />
            }
            errorMessage={
              errorId && (
                <FormattedHTMLMessage
                  id={`errorId.${errorId}`}
                  defaultMessage={`Invalid ${config.fieldId}`}
                />
              )
            }
          />
        );
      case FormFieldConfigType.SELECT:
        return (
          <FormFieldSelect
            fieldId={config.fieldId}
            value={`${currentValue || ""}`}
            onChange={onChange}
            options={getFormFieldSelectOptions(config.fieldId, config.options, intl)}
            disabled={config.disabled}
            placeholder={intl.formatHTMLMessage({
              id: `${config.fieldId}Placeholder`,
              defaultMessage: config.fieldId,
            })}
            label={
              <FormattedHTMLMessage
                id={getMessageIdForLabel(config)}
                defaultMessage={config.fieldId}
              />
            }
            errorMessage={
              errorId && (
                <FormattedHTMLMessage
                  id={`errorId.${errorId}`}
                  defaultMessage={`Invalid ${config.fieldId}`}
                />
              )
            }
            showPlaceholderAndHideLabel={config.showPlaceholderAndHideLabel}
            required={config.required}
          />
        );
      case FormFieldConfigType.DATE:
      case FormFieldConfigType.DATEYM:
        const mode = config.fieldType === FormFieldConfigType.DATEYM ? "month" : undefined;
        return (
          <DateComponent
            fieldId={config.fieldId}
            fieldName={config.fieldId}
            value={`${currentValue || ""}`}
            onChange={onChange}
            disabled={config.disabled}
            label={
              <FormattedHTMLMessage
                id={getMessageIdForLabel(config)}
                defaultMessage={config.fieldId}
              />
            }
            explanation={
              getSafe(() => intl.messages[getMessageIdForExplanation(config)]) && (
                <FormattedHTMLMessage id={getMessageIdForExplanation(config)} />
              )
            }
            isErrored={!!errorId}
            errorMsg={
              <FormattedHTMLMessage
                id={`errorId.${errorId}`}
                defaultMessage={`Invalid ${config.fieldId}`}
              />
            }
            isRequired={config.required}
            mode={mode}
          />
        );
      case FormFieldConfigType.RADIO:
        return (
          <FormFieldRadio
            onChange={onChange}
            value={`${currentValue || ""}`}
            options={getFormFieldSelectOptions(config.fieldId, config.options, intl)}
            required={config.required}
            fieldId={config.fieldId}
            disabled={config.disabled}
            label={
              <FormattedHTMLMessage
                id={getMessageIdForLabel(config)}
                defaultMessage={config.fieldId}
              />
            }
            explanation={
              getSafe(() => intl.messages[getMessageIdForExplanation(config)]) && (
                <FormattedHTMLMessage id={getMessageIdForExplanation(config)} />
              )
            }
            errorMessage={
              errorId && (
                <FormattedHTMLMessage
                  id={`errorId.${errorId}`}
                  defaultMessage={`Invalid ${config.fieldId}`}
                />
              )
            }
          />
        );
      default:
        logger.warn(`Unknown field type: ${config.fieldType}`);
        return null;
    }
  },
);
